import React, { useState } from "react";
import "./ParticipantAddContactsForm.css";
import {
  sanitizeEmailInput,
  sanitizePhoneNumber,
  decodeQueryParameter,
} from "../../CommonFunctions";
import config from "../../config";
import {
  FundraiserAddParticipantContactsResponse,
  FundraiserAddParticipantContactsRequest,
  Contact,
} from "../../Interfaces/FundraiserRole";
import { usePopup } from "../Popup/PopupContext";
import PopupModel from "../Popup/PopupModel";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import apiClient from "../../services/apiClient";
import axios from "axios";

interface ContactRowProps {
  index: number;
  contact: Contact;
  onAdd: () => void;
  onRemove: () => void;
  onChange: (index: number, field: keyof Contact, value: string) => void;
  isPhoneNumberEnabled: boolean;
  errorMessages: {
    customer_name?: string;
    customer_email?: string;
    customer_phone?: string;
    general?: string;
  };
}

const ContactRow: React.FC<ContactRowProps> = ({
  index,
  contact,
  onAdd,
  onRemove,
  onChange,
  isPhoneNumberEnabled,
  errorMessages,
}) => {
  return (
    <>
      <tr className={errorMessages.general ? "error-row" : ""}>
        <td>{index + 1}</td>
        <td>
          <input
            type="text"
            value={contact.customer_name}
            onChange={(e) => onChange(index, "customer_name", e.target.value)}
          />
          {errorMessages.customer_name && (
            <div className="error-message">{errorMessages.customer_name}</div>
          )}
        </td>
        <td>
          <input
            type="email"
            value={contact.customer_email || ""}
            onChange={(e) => onChange(index, "customer_email", e.target.value)}
          />
          {errorMessages.customer_email && (
            <div className="error-message">{errorMessages.customer_email}</div>
          )}
        </td>
        {isPhoneNumberEnabled && (
          <td>
            <input
              type="text"
              value={contact.customer_phone || ""}
              onChange={(e) =>
                onChange(index, "customer_phone", e.target.value)
              }
            />
            {errorMessages.customer_phone && (
              <div className="error-message">
                {errorMessages.customer_phone}
              </div>
            )}
          </td>
        )}
        <td>
          <button
            onClick={onRemove}
            className="participant-add-contacts-remove"
          >
            ➖
          </button>
        </td>
      </tr>
      {errorMessages.general && (
        <tr className="error-row">
          <td
            colSpan={isPhoneNumberEnabled ? 4 : 3}
            className="general-error-message"
          >
            {errorMessages.general}
          </td>
        </tr>
      )}
    </>
  );
};

const ContactForm: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>(
    new Array(10).fill(null).map(() => ({
      customer_name: "",
      customer_email: null,
      customer_phone: null,
    }))
  );
  const [errorMessages, setErrorMessages] = useState<
    Map<
      number,
      {
        customer_name?: string;
        customer_email?: string;
        customer_phone?: string;
        general?: string;
      }
    >
  >(new Map());
  const isPhoneNumberEnabled = config.IS_PHONE_NUMBER_SUPPORT_ENABLED;
  const BASE_API = config.API_BASE_URL;
  const { participantIds } = useParams<{ participantIds: string }>();
  const navigate = useNavigate();
  const { showPopup, hidePopup } = usePopup();
  const handleAddRow = () => {
    setContacts([
      ...contacts,
      { customer_name: "", customer_email: "", customer_phone: "" },
    ]);
  };

  const handleRemoveRow = (index: number) => {
    if (contacts.length > 1) {
      const newContacts = contacts.filter((_, i) => i !== index);
      setContacts(newContacts);
      setErrorMessages((prev) => {
        const newErrorMessages = new Map(prev);
        newErrorMessages.delete(index);
        return newErrorMessages;
      });
    }
  };

  const handleChange = (index: number, field: keyof Contact, value: string) => {
    const newContacts = [...contacts];

    newContacts[index] = {
      ...newContacts[index],
      [field]: value,
    };

    setContacts(newContacts);
  };
  const trimAndSanitizeContact = (
    contact: Contact
  ): { trimmed: Contact; errors: { [key: string]: string } } => {
    const errors: { [key: string]: string } = {};
    const trimmedName = contact.customer_name?.trim() || "";
    const trimmedEmail = contact.customer_email?.trim() || "";
    const trimmedPhone = contact.customer_phone?.trim() || "";

    if (!trimmedName) {
      errors.customer_name = "Name is required.";
    }
    const hasEmail = !!trimmedEmail;
    const hasPhone = isPhoneNumberEnabled && !!trimmedPhone;

    if (!hasEmail && !hasPhone) {
      errors.customer_email = "Please enter a valid email";
      errors.customer_phone = "Please enter a valid phone number";
    }
    if (hasEmail) {
      try {
        sanitizeEmailInput(trimmedEmail);
      } catch {
        errors.customer_email = "Invalid email format.";
      }
    }
    if (hasPhone) {
      try {
        sanitizePhoneNumber(trimmedPhone);
      } catch {
        errors.customer_phone = "Invalid phone number format.";
      }
    }

    return {
      trimmed: {
        customer_name: trimmedName,
        customer_email: hasEmail ? trimmedEmail : null,
        customer_phone: hasPhone ? trimmedPhone : null,
      },
      errors,
    };
  };

  // Filter out empty contacts
  const getFilteredContacts = () =>
    contacts.filter(
      (contact) =>
        contact.customer_name?.trim() ||
        contact.customer_email?.trim() ||
        contact.customer_phone?.trim()
    );

  const validateContacts = (): [boolean, Contact[]] => {
    const filteredContacts = getFilteredContacts();

    // If all rows are empty, ensure one empty row remains
    if (filteredContacts.length === 0) {
      setContacts([
        { customer_name: "", customer_email: "", customer_phone: "" },
      ]);
      setErrorMessages(new Map());
      return [false, []];
    }

    const newErrorMessages = new Map<number, { [key: string]: string }>();
    let hasErrors = false;
    const sanitizedContacts: Contact[] = [];

    filteredContacts.forEach((contact, idx) => {
      const { trimmed, errors } = trimAndSanitizeContact(contact);
      sanitizedContacts.push(trimmed);
      if (
        errors.customer_name ||
        errors.customer_email ||
        errors.customer_phone ||
        errors.general
      ) {
        newErrorMessages.set(idx, errors);
        hasErrors = true;
      }
    });

    setContacts(sanitizedContacts);
    setErrorMessages(newErrorMessages);
    return [!hasErrors, sanitizedContacts];
  };

  const handleContinue = () => {
    navigate("/dashboard/user");
  };

  const handlePopup = (successful_emails: string[]) => {
    const emailList = (
      <ul>
        {successful_emails.map((email, index) => (
          <li key={index}>{email}</li>
        ))}
      </ul>
    );

    const checkInfoAndContinuePopup = new PopupModel(
      "Adding Contact Info for Participants",
      (
        <div>
          The following emails will be added to your participant contact lists:
          {emailList}
        </div>
      ),
      [
        {
          label: "Continue to my dashboard",
          onClick: () => {
            navigate("/dashboard/user");
            hidePopup();
          },
        },
        {
          label: "Keep adding contacts",
          onClick: () => {
            setContacts([
              { customer_name: "", customer_email: null, customer_phone: null },
            ]);
            hidePopup();
          },
        },
      ],
      "40vw"
    );

    showPopup(
      checkInfoAndContinuePopup.header,
      checkInfoAndContinuePopup.body,
      checkInfoAndContinuePopup.buttons,
      checkInfoAndContinuePopup.width
    );
  };

  const handleSomeEmailsPopup = (
    successful_emails: string[],
    failed_emails: string[],
    in_use_emails: string[]
  ) => {
    const emailList = (
      <ul>
        {successful_emails.map((email, index) => (
          <li key={index}>{email}</li>
        ))}
      </ul>
    );
    const badEmailList = (
      <ul>
        {failed_emails.map((email, index) => (
          <li key={index}>{email}</li>
        ))}
        {in_use_emails.map((email, index) => (
          <li key={index}>{email}</li>
        ))}
      </ul>
    );

    const checkInfoAndContinuePopup = new PopupModel(
      "Adding Contact Info for Participants",
      (
        <div>
          {successful_emails.length > 0 && (
            <div>
              The following emails <strong>were</strong> added to your
              participant contact lists:
              {emailList}
            </div>
          )}
          The following emails <strong>could not</strong> be added to your
          participant contact lists:
          {badEmailList}
        </div>
      ),
      [
        {
          label: "Continue to my dashboard",
          onClick: () => {
            navigate("/dashboard/user");
            hidePopup();
          },
        },
        {
          label: "Keep adding contacts",
          onClick: () => {
            hidePopup();
          },
        },
      ],
      "40vw"
    );

    showPopup(
      checkInfoAndContinuePopup.header,
      checkInfoAndContinuePopup.body,
      checkInfoAndContinuePopup.buttons,
      checkInfoAndContinuePopup.width
    );
  };

  const handleSave = async () => {
    const [isValid, filteredContacts] = validateContacts();
    if (!isValid) return;
    try {
      const part_id = decodeQueryParameter(participantIds || "");
      const submission_data: FundraiserAddParticipantContactsRequest = {
        fundraiser_participant: part_id
          ? part_id.participants.split(",").map(Number)
          : [-1],
        contacts: filteredContacts,
      };

      const response =
        await axios.post<FundraiserAddParticipantContactsResponse>(
          BASE_API + "fundraiser-role/participant-contacts/",
          submission_data,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

      const fundraiserContactsResponse: FundraiserAddParticipantContactsResponse =
        response.data;
      const { successful_emails, failed_emails, in_use_emails } =
        fundraiserContactsResponse;

      if (failed_emails.length === 0 && in_use_emails.length === 0) {
        // If there are no failed emails, navigate to the next page
        handlePopup(successful_emails);
      } else {
        // Filter out successful emails
        const remainingContacts = contacts.filter(
          (contact) =>
            failed_emails.includes(contact.customer_email || "") ||
            in_use_emails.includes(contact.customer_email || "")
        );

        if (remainingContacts.length > 0) {
          setContacts(remainingContacts);
        } else {
          setContacts([
            { customer_name: "", customer_email: null, customer_phone: null },
          ]);
        }

        // Update error messages for failed emails
        const newErrorMessages = new Map<
          number,
          {
            customer_name?: string;
            customer_email?: string;
            customer_phone?: string;
            general?: string;
          }
        >();
        remainingContacts.forEach((contact, index) => {
          if (failed_emails.includes(contact.customer_email || "")) {
            newErrorMessages.set(index, {
              customer_email: "This email has requested to not be emailed",
            });
          } else if (in_use_emails.includes(contact.customer_email || "")) {
            newErrorMessages.set(index, {
              customer_email:
                "This email is already associated with this participant",
            });
          }
        });

        setErrorMessages(newErrorMessages);
        handleSomeEmailsPopup(successful_emails, failed_emails, in_use_emails);
      }
    } catch (error) {
      console.error("Error submitting contacts:", error);
    }
  };

  return (
    <div className="participant-add-contacts-container">
      <div className="participant-add-contacts-table-wrapper">
        <table className="participant-add-contacts-table">
          <thead>
            <tr>
              <th className="participant-add-contacts-th">No</th>
              <th className="participant-add-contacts-th">Name</th>
              <th className="participant-add-contacts-th">E-mail</th>
              {isPhoneNumberEnabled && (
                <th className="participant-add-contacts-th">Mobile Phone #</th>
              )}
            </tr>
          </thead>
          <tbody>
            {contacts.map((contact, index) => (
              <ContactRow
                key={index}
                index={index}
                contact={contact}
                onAdd={handleAddRow}
                onRemove={() => handleRemoveRow(index)}
                onChange={handleChange}
                isPhoneNumberEnabled={isPhoneNumberEnabled}
                errorMessages={errorMessages.get(index) || {}}
              />
            ))}
          </tbody>
        </table>
        <div className="participant-add-contacts-fab" onClick={handleAddRow}>
          ➕
        </div>
      </div>
      <div className="participant-add-contacts-button-wrapper">
        <button
          className="participant-add-contacts-save-button"
          onClick={handleSave}
        >
          Save and Continue
        </button>
        <button
          className="participant-add-contacts-save-button"
          onClick={handleContinue}
        >
          Continue without adding contacts
        </button>
      </div>
    </div>
  );
};

export default ContactForm;
